import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityPrefix } from "building-types/handles/docEntityPrefix";
import { DocEntityPrefixToDocEntityTypes } from "building-types/handles/docEntityPrefix";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { docGetEntityHandleById } from "./docGetEntityHandleById";

/**
 * Depends on the last string in array containing the entity id.
 */
const DocEntityHashDelimiter = "." as const;

export type DocEntityHandleHash<T extends DocEntityPrefix = DocEntityPrefix> = `${T}.${string}`;

export function parseEntityIdFromHandleHash(hash: string): string | null {
  const result = hash.split(DocEntityHashDelimiter);
  if (result.length === 1) {
    return null;
  }
  const length = result.length;
  if (result[0] === "edge") {
    return null;
  }
  return result[length - 1];
}

export function parseEntityInfoFromHandleHash(hash: string): {
  entityId: string;
  entityType: DocEntityType;
  entityTypePrefix: DocEntityPrefix;
} | null {
  const result = hash.split(DocEntityHashDelimiter);

  if (result.length === 1) {
    return null;
  }

  if (result[0] === "edge") {
    return null;
  }

  const [entityTypePrefix] = result as [DocEntityPrefix];

  if (!entityTypePrefix || !DocEntityPrefixToDocEntityTypes[entityTypePrefix]) {
    return null;
  }

  const [entityType] = DocEntityPrefixToDocEntityTypes[entityTypePrefix];

  const entityId = result.at(-1);

  assertIsNotUndefined(entityId);
  return { entityId, entityType, entityTypePrefix };
}

export function docGetEntityHandleByHash<T extends DocEntityHandle>(doc: Doc, hash: string): T | null {
  const result = parseEntityInfoFromHandleHash(hash);
  if (!result) {
    return null;
  }
  const { entityId, entityTypePrefix } = result;
  if (!entityId) {
    return null;
  }

  return docGetEntityHandleById(doc, entityId, DocEntityPrefixToDocEntityTypes[entityTypePrefix]) as T;
}

export function isDocEntityHandleHash(hash: any): hash is DocEntityHandleHash {
  const prefix =
    typeof hash === "string" && hash.includes(DocEntityHashDelimiter) ? hash.split(DocEntityHashDelimiter)[0] : null;
  if (!prefix || prefix === "edge") {
    return false;
  }
  return prefix in DocEntityPrefixToDocEntityTypes;
}
